import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useKeyOperation } from 'components/WorkspaceEditor/features/keyOperations/hooks/useKeyOperation'
import { useCKEditorToggle } from 'components/WorkspaceEditor/hooks'
import { useContentEditorFocus } from 'components/WorkspaceEditor/hooks/useContentEditorFocus'
import { useTransactionManager } from 'components/WorkspaceEditor/services'
import { useCallback, useMemo, useState } from 'react'
import {
    ClassicEditorHandler,
    ClassicEditorOnReadyHandler,
} from '../ContentEditor'
import {
    extractSuggestions,
    getUnresolvedChanges,
} from '../ContentEditor/contentUtils'
import { useFormatReplacementOption } from './hooks/useFormatReplacementOption'

/** Event handlers for ReplacementOption CKEditor interactions */
interface ReplacementOptionHandler {
    /**
     * Handler called when CKEditor is ready and initialized.
     * Sets the editor reference for transaction management and rollback capabilities.
     */
    onReady: ClassicEditorOnReadyHandler

    /**
     * Handler called when CKEditor loses focus.
     * - Deactivates the editor when there are no content changes
     * - Processes and saves changes when content has been modified
     * - Triggers optimistic mutations for immediate UI updates
     */
    onBlur: ClassicEditorHandler

    /**
     * Handler for keyboard events while CKEditor has focus.
     * Handles special key combinations:
     * - Enter: Save current content and add new content
     * - Ctrl+S: Save content only
     * - Escape: Deactivate editor and revert changes
     */
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void

    /**
     * Handler called when CKEditor content changes.
     */
    onChange: ClassicEditorHandler

    /**
     * Handler called when CKEditor receives focus.
     * Activates the editor to enable content editing.
     */
    onFocusCapture: ClassicEditorHandler
}

/** Constants */
const OPTION_ITEM_STATE = {
    INACTIVE: 'InActive',
} as const

/**
 * A hook that provides controller functionality for replacement options in the editor.
 * It handles focus, blur, and change events for a replacement option component.
 *
 * @param {Partial<TextContentElement>} contentItem - The content item representing the replacement option
 * @param {Function} [onValueChange] - Optional callback that's called when the content item's value changes
 * @returns {Object} An object containing event handlers for the replacement option component
 */
export const useReplacementOptionController = (
    contentItem: Partial<TextContentElement>,
    onValueChange?: (
        currentSelectedItem: Partial<TextContentElement>,
        currentlySelected: boolean
    ) => void
) => {
    const { registerEditorRef, editorRef } = useTransactionManager()
    const {
        getUpdatedContentItem,
        removeUnitOfMeasureDelimiters,
    } = useFormatReplacementOption(contentItem)
    // State to track unsaved content changes in the editor
    const [unsavedData, setUnsavedData] = useState<
        string | Record<string, string>
    >(contentItem.text ?? '')

    // Flag to determine if new content should be added after saving
    const [shouldTriggerNewContent, setShouldTriggerNewContent] = useState(
        false
    )
    // Create unique identifier for editor tracking - no memoization needed for simple string concatenation
    const formattedContentId = `replacement-option-${
        contentItem.id || 'default'
    }`
    const ckeToggle = useCKEditorToggle(formattedContentId)

    const initialSuggestions = useMemo(
        () => extractSuggestions([contentItem]),
        [contentItem]
    )

    const { editorFocusRef, focusEditor } = useContentEditorFocus({
        contentItemId: contentItem.id,
        activate: ckeToggle.activate,
        controlType: 'replacementOption',
    })

    /**
     * Handles the focus capture event for the replacement option.
     * Activates the CKEditor when the element receives focus.
     */
    const handleOnFocusCapture = useCallback(() => {
        // Use the new focusEditor function instead of directly activating
        focusEditor()
    }, [focusEditor, contentItem])

    /**
     * Handles the blur event for the CKEditor.
     * Extracts plain text from the editor's HTML content, updates the content item if changed,
     * and calls the onValueChange callback if provided.
     *
     * @param {FocusEvent} event - The blur event
     * @param {Object} editor - The CKEditor instance
     */
    /**
     * Handles the blur event when CKEditor loses focus.
     * Processes content changes, updates the content item, and deactivates the editor.
     */
    const handleBlur = useCallback(
        (_, editor) => {
            const currentContent = unsavedData.toString()
            const hasNoChanges = currentContent === contentItem.text

            // Early return if no changes were made - simply deactivate the editor
            if (hasNoChanges) {
                return ckeToggle.deactivate()
            }

            // Handle empty content by falling back to placeholder text if available
            const isContentEmpty = currentContent.trim() === ''
            const hasPlaceholderText = Boolean(contentItem.placeholderText)
            const finalText =
                isContentEmpty && hasPlaceholderText
                    ? contentItem.placeholderText!
                    : currentContent

            // Create updated content item with processed text and elements
            const updatedContentItem: Partial<TextContentElement> = {
                ...contentItem,
                text: removeUnitOfMeasureDelimiters(finalText),
                placeholderText: removeUnitOfMeasureDelimiters(
                    contentItem.placeholderText
                ),
                elements: getUpdatedContentItem(contentItem),
            }

            // Notify parent component of the content change
            onValueChange?.(updatedContentItem, false)

            // Deactivate the editor
            ckeToggle.deactivate()
        },
        [
            contentItem,
            onValueChange,
            ckeToggle,
            unsavedData,
            removeUnitOfMeasureDelimiters,
            getUpdatedContentItem,
        ]
    )
    const {
        handleDeactivateAndAddContentReplacementOption,
        executeSaveAndAddContent,
        executeSaveOnly,
    } = useKeyOperation({
        editorRef,
        contentItemId: contentItem.id,
        handleOnBlur: handleBlur,
        shouldTriggerNewContent,
        setShouldTriggerNewContent,
        unFocusEditor: ckeToggle.deactivate,
    })

    const handleOnChange = useCallback(
        (_, editor) => {
            const hasInitialSuggestions = initialSuggestions.length > 0

            const textChanges = getUnresolvedChanges(
                editor.getData(),
                contentItem.text,
                hasInitialSuggestions
            )
            setUnsavedData(textChanges)
        },
        [initialSuggestions, contentItem.text]
    )

    /**
     * Handles keyboard events for the replacement option.
     * Manages save operations, content addition, and editor deactivation based on key combinations.
     */
    const handleOnKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            // Handle save-only operations (e.g., Ctrl+S)
            if (executeSaveOnly(event)) {
                return
            }

            // Determine if content has been modified
            const hasNoChanges =
                typeof unsavedData === 'string' &&
                unsavedData.toString() === contentItem.text

            // Set flag to indicate new content should be triggered
            setShouldTriggerNewContent(true)

            // Case 1: Handle immediate deactivation and content addition (e.g., Escape key)
            if (
                handleDeactivateAndAddContentReplacementOption(
                    event,
                    hasNoChanges
                )
            ) {
                return
            }

            // Case 2: Handle save and add new content (e.g., Enter key)
            if (executeSaveAndAddContent(event)) {
                return
            }

            // Reset the trigger flag if no special operation was performed
            setShouldTriggerNewContent(false)
        },
        [
            executeSaveOnly,
            unsavedData,
            contentItem.text,
            handleDeactivateAndAddContentReplacementOption,
            executeSaveAndAddContent,
        ]
    )

    // Create onReady handler that sets up editor references
    const onReady = useCallback(
        (editor) => {
            editorFocusRef.current = editor
            registerEditorRef(editor)
        },
        [editorFocusRef, registerEditorRef]
    )

    // Return individual handler functions - simpler than memoized object
    return {
        handlers: {
            onReady,
            onChange: handleOnChange,
            onBlur: handleBlur,
            onFocusCapture: handleOnFocusCapture,
            onKeyDown: handleOnKeyDown,
        },
    }
}
